"use client";

import { memo, useCallback, useEffect, useState } from "react";
import { RECORD_WINDOW_TYPES, WindowState } from "./types";
import { useWindowManager } from "./useWindowManager";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { ISLAND_LAYOUT, STAGE_LAYOUT } from "./desktopUI.constants";
import { useSideChatInset } from "./useSideChatInset";
import { Button, Glyph, Keycap, PageTitle, Tooltip } from "@/components/desktop/ui";
import { PageStagedContext, TopBarSlot } from "./TopBar";
import { cn } from "@/lib/utils";
import { useIsPhone } from "@/hooks/useIsPhone";
import { PaneBackContext, usePaneBackStack } from "./ui/usePaneBack";
import { IslandLaneContext } from "./ui/islandLane.utils";
import { registerWindowPaneBack } from "./paneBack.utils";
import { getStageBackTarget, useDesktopStore } from "@/stores/desktopStore";
import { isExplorerSection } from "./windows/explorer/explorer.utils";
import { WINDOW_REGISTRY_METADATA as WINDOW_REGISTRY } from "./windowRegistry.metadata";
interface WindowProps {
  window: WindowState;
  children: React.ReactNode;
  isStaged?: boolean;
}
export const Window = memo(function Window({
  window: windowState,
  children,
  isStaged = true
}: WindowProps) {
  const tA11y = useClientTranslations("desktop.a11y");
  const tPage = useClientTranslations("desktop.page");
  const tApps = useClientTranslations("desktop.setup.apps");
  const {
    activeWindowId,
    windows,
    closeWindow,
    focusWindow,
    stageBack
  } = useWindowManager();
  const stageBackWalkIds = useDesktopStore(state => state.stageBackWalkIds);
  const isPhone = useIsPhone();
  const sideChatInset = useSideChatInset();
  const {
    registry,
    openPane,
    closeOpenPane
  } = usePaneBackStack();
  const isActive = activeWindowId === windowState.id;
  const isRecord = RECORD_WINDOW_TYPES.has(windowState.type);
  const isBesideIsland = !isPhone && (isExplorerSection(windowState.type) || WINDOW_REGISTRY[windowState.type].islandFit === "lane");
  const isMinimized = windowState.displayState === "minimized";
  const [hasBeenShown, setHasBeenShown] = useState(!isMinimized);
  if (!isMinimized && !hasBeenShown) setHasBeenShown(true);
  const handleMouseDown = useCallback(() => {
    if (!isActive) focusWindow(windowState.id);
  }, [isActive, focusWindow, windowState.id]);
  const handleClose = useCallback(() => closeWindow(windowState.id), [windowState.id, closeWindow]);
  const handleBack = useCallback(() => {
    if (closeOpenPane()) return;
    stageBack();
  }, [closeOpenPane, stageBack]);
  useEffect(() => registerWindowPaneBack({
    windowId: windowState.id,
    closeOpenPane
  }), [windowState.id, closeOpenPane]);
  if (isMinimized && !hasBeenShown) return null;
  const backTarget = getStageBackTarget(windows, windowState.id, stageBackWalkIds);
  const originLabel = backTarget && backTarget.id === windowState.data?.originWindowId && typeof windowState.data?.originLabel === "string" ? windowState.data.originLabel : undefined;
  const backLabel = originLabel ?? (backTarget ? RECORD_WINDOW_TYPES.has(backTarget.type) ? backTarget.title : tApps(backTarget.type) : tPage("desktop"));
  const backToLabel = tPage("backTo", {
    place: openPane?.label ?? backLabel
  });
  return <PageStagedContext.Provider value={isStaged}>
            {(backTarget || openPane || isPhone) && <TopBarSlot slot="back">
                    <Tooltip side="bottom" className="flex-shrink-0" shouldPassEscapeThrough content={<span className="flex items-center gap-1.5">
                                {backToLabel}
                                <Keycap>esc</Keycap>
                            </span>}>
                        <Button variant="control" size="icon" onClick={handleBack} aria-label={backToLabel} aria-keyshortcuts={isPhone ? undefined : "Escape"}>
                            <Glyph name="chevronLeft" className="h-4 w-4 flex-shrink-0" />
                        </Button>
                    </Tooltip>
                </TopBarSlot>}
            {isRecord ? <TopBarSlot slot="left" subject={windowState.title}>
                    <PageTitle placement="bar" variant="record">
                        {windowState.title}
                    </PageTitle>
                </TopBarSlot> : isPhone && openPane?.title ? <TopBarSlot slot="left">
                    <PageTitle placement="bar">{openPane.title}</PageTitle>
                </TopBarSlot> : (!WINDOW_REGISTRY[windowState.type].isSelfTitled || isPhone && !openPane) && <TopBarSlot slot="left" subject={tApps(windowState.type)}>
                        <PageTitle placement="bar">
                            {tApps(windowState.type)}
                        </PageTitle>
                    </TopBarSlot>}
            {isRecord && <TopBarSlot slot="controls">
                    <Button variant="control" size="icon" onClick={handleClose} aria-label={tA11y("closeWindow")} title={tA11y("closeWindow")}>
                        <Glyph name="close" className="h-4 w-4" />
                    </Button>
                </TopBarSlot>}
            <div className={cn("desktop-window absolute left-0 flex flex-col pointer-events-auto", WINDOW_REGISTRY[windowState.type].isOffwhite ? "bg-offwhite" : "bg-surface", !isStaged && "invisible")} aria-hidden={!isStaged} inert={!isStaged} data-window-id={windowState.id} style={{
      top: STAGE_LAYOUT.TOP,
      bottom: STAGE_LAYOUT.BOTTOM,
      right: sideChatInset,
      zIndex: windowState.zIndex
    }} onMouseDownCapture={handleMouseDown}>
                <div className="min-h-0 flex-1 overflow-hidden" style={{
        paddingTop: isBesideIsland ? 0 : ISLAND_LAYOUT.CLEARANCE
      }}>
                    <IslandLaneContext.Provider value={isBesideIsland}>
                        <PaneBackContext.Provider value={registry}>
                            {children}
                        </PaneBackContext.Provider>
                    </IslandLaneContext.Provider>
                </div>
            </div>
        </PageStagedContext.Provider>;
}, windowPropsAreEqual);
function windowPropsAreEqual(prevProps: WindowProps, nextProps: WindowProps): boolean {
  if (prevProps.window !== nextProps.window) {
    const prevWin = prevProps.window;
    const nextWin = nextProps.window;
    if (prevWin.id !== nextWin.id || prevWin.displayState !== nextWin.displayState || prevWin.zIndex !== nextWin.zIndex || prevWin.title !== nextWin.title || prevWin.data !== nextWin.data) {
      return false;
    }
  }
  return prevProps.isStaged === nextProps.isStaged;
}